import { z, ZodTypeAny } from 'zod';

import { PcfraDocumentType } from '../types/pcfra';
import { userSchema } from './app';
import { nonEmptyString, presentOrPastDateSchema } from './data';
import { anyPhoneNumberSchema, camdenEmailAddressSchema, contactDetailsFormSchema } from './people';

export const pcfraActivityTypeSchema = z.enum([
  'failed-contact',
  'successful-contact',
  'self-referral-rejected',
  'self-referral-accepted',
  'rpeep-completed',
  'rpeep-revoked',
]);

export const pcfraDocumentTypeSchema = z.enum(PcfraDocumentType);

/** Source of truth for the `pcfraDocuments` user store table (see userStoreSchema.ts) */
export const pcfraDocumentSchema = z.object({
  documentId: z.number(),
  /** Property the document is associated with */
  documentPropertyId: z.number(),
  personId: z.number(),
  /** Person's current residence */
  personPropertyId: z.number(),
  documentType: pcfraDocumentTypeSchema,
  isActive: z.boolean(),
  title: z.string(),
  url: z.string(),
  created: z.string(),
});

const falseOrSchema = <TSchema extends ZodTypeAny>(schema: TSchema) =>
  z.union([z.literal(false, { message: 'Required' }), schema]);

const falseOrMultiSelectSchema = falseOrSchema(
  z
    .array(z.string())
    .min(1, 'Must select one or more options')
    .refine(
      // Tests for empty custom option values that are in the format "{optionId}:{customValue}"
      (value) => !value.some((element) => /\w+:$/.test(element)),
      'A selected option requires clarification',
    ),
);

const falseOrStringSchema = falseOrSchema(
  z.string().min(3, 'Please specify in the provided text box'),
);

export const residentRepresentativeSchema = z.object(
  {
    name: nonEmptyString,
    relationshipToResident: nonEmptyString,
    emailAddress: z.email().optional(),
    telephoneNumber: anyPhoneNumberSchema,
  },
  { message: "Please provide the representative's details" },
);

/** Source of truth for the `pcfraReferrers` user store table (see userStoreSchema.ts) */
export const pcfraReferrerSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
  emailAddress: camdenEmailAddressSchema,
});

export const deletePcfraReferrerInputSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
});

export const pcfraDetailsSchema = z.object({
  pcfraReference: nonEmptyString,
  createdBy: nonEmptyString,
  uprn: nonEmptyString,
  floorLevel: nonEmptyString,
  contactDetails: contactDetailsFormSchema,
  residentRepresentative: falseOrSchema(residentRepresentativeSchema),
  referrerEmailAddress: z.union([camdenEmailAddressSchema, z.literal('')]).optional(),
  dateOfVisit: presentOrPastDateSchema(),
});

export const fireSafetySurveySchema = z.object({
  fireRiskFactors: falseOrMultiSelectSchema,
  reactionRiskFactors: falseOrMultiSelectSchema,
  escapeRiskFactors: falseOrMultiSelectSchema,
  smokeOrHeatAlarms: falseOrStringSchema,
  carbonMonoxideAlarm: falseOrStringSchema,
  comments: z.string().optional(),
});

export const whatToDoNextSchema = z.object({
  hasCompletedAllActions: z.boolean(),
});

export const severitySchema = z.enum(['Low', 'Medium', 'High']);

export const pcfraRecommendationSchema = z.object({
  type: z.string(),
  priority: severitySchema,
  notes: z.string().min(3, 'Please specify what action should be taken'),
});

export const actionsRecommendationsSchema = z.object({
  recommendations: z.array(pcfraRecommendationSchema).optional(),
  comments: z.string().optional(),
  reviewDate: z.iso.date(),
});

export const riskToResidentPremisesSchema = z.object({
  fireLikelihood: severitySchema,
  harmPotential: severitySchema,
  currentRiskLevel: z.number(),
});

export const pcfraValuesSchema = z.object({
  details: pcfraDetailsSchema,
  fireSafetySurvey: fireSafetySurveySchema,
  whatToDoNext: whatToDoNextSchema,
  actionsRecommendations: actionsRecommendationsSchema,
  riskToResidentPremises: riskToResidentPremisesSchema,
});

export const pcfraAuthorSchema = userSchema.pick({
  id: true,
  name: true,
  email: true,
  camdenAccountName: true,
});

/**
 * Source of truth for the `pcfraDrafts` user store table (see
 * userStoreSchema.ts)
 */
export const pcfraDraftSchema = z.object({
  draftId: z.string(),
  propertyId: z.number(),
  personId: z.number(),
  values: z.record(z.string(), z.any()),
  createdBy: pcfraAuthorSchema,
  modifiedBy: pcfraAuthorSchema.optional(),
  created: z.iso.datetime({ offset: true }),
  modified: z.iso.datetime({ offset: true }),
});

export const submitPcfraInputSchema = z.object({
  pcfraUrl: z.string(),
});

export const approvePcfraInputSchema = z.object({
  personId: z.number(),
  pcfraValues: pcfraValuesSchema,
});

export const deletePcfraDraftInputSchema = z.object({
  propertyId: z.number(),
  draftId: z.string(),
});

export const syncPcfraDocumentsQuerySchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
});

export const revokePcfraInputSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
});
